import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useAssessmentContext } from '../AssessmentContext';
import {
  useReactTable,
  flexRender
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/table-core';
import { 
  FiSave, 
  FiSend, 
  FiTrash2, 
  FiEye,
  FiCheck,
  FiChevronDown,
  FiBox,
  FiPlus,
  FiVideo,
  FiShare2,
  FiFileText,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import BatchRow from './BatchRow';
import GroupsInlineSearchField from './GroupsInlineSearchField';
import RoleSelectionSummary from './RoleSelectionSummary';
import { resolveAssignToSelectionNames } from './resolveAssignToSelectionNames';
import { GroupsPickerMemberRow } from './GroupsPickerDropdownPanel';
import GroupsPickerDropdownPortal from './GroupsPickerDropdownPortal';
import { getEffectivePendingEvalCount } from '../../../utils/assessmentEval';
import AssessmentPopup from './AssessmentPopup';
import { AssessmentEditIcon } from '../AssessmentContext';
import QuestionPreviewModal from './QuestionPreviewModal';
import IntroVideoPreviewModal from './IntroVideoPreviewModal';
import { isAssessmentFlagOn, normalizeIntroVideosList } from '../../../utils/assessmentStudent';

import testService from '../../../services/testService';

const GROUPS_PAGE_SIZE = 3;

const isHrCategory = (category, categoryId) => {
  if (categoryId) {
    return [2, 4].includes(Number(categoryId));
  }
  const cat = String(category || '').trim();
  return cat === 'HR Recruitment-Fresher' || cat === 'External';
};

const filterAssessmentPacks = (testPacks, row, activeFormId, savedPackId, publishedPackRow) =>
  (testPacks ?? []).filter((pack) => {
    if (activeFormId === row.original.id && savedPackId && String(pack.id) === String(savedPackId)) {
      return false;
    }
    if (
      publishedPackRow?.assessmentId === row.original.id &&
      String(pack.id) === String(publishedPackRow.packId)
    ) {
      return false;
    }
    return true;
  });

const sortAssessmentPacks = (packs, subSortConfig) =>
  [...packs].sort((a, b) => {
    let valA = a[subSortConfig.key];
    let valB = b[subSortConfig.key];
    if (subSortConfig.key === 'duration' || subSortConfig.key === 'id') {
      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
    }
    if (subSortConfig.direction === 'asc') {
      return valA > valB ? 1 : -1;
    }
    return valA < valB ? 1 : -1;
  });

const GroupsAccordionPagination = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  if (totalItems <= pageSize) {
    return null;
  }

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages =
    pages.length <= 7
      ? pages
      : pages.filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1);

  return (
    <div className="assess-mod-groups-accordion-pagination px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 bg-white">
      <div className="assess-mod-inventory-pagination text-sm font-semibold text-slate-500">
        Showing {start} to {end} of {totalItems} groups
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
            currentPage <= 1
              ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed'
              : 'bg-white border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500 shadow-sm'
          }`}
        >
          <FiChevronLeft className="text-xs" />
          Previous
        </button>
        {visiblePages.map((page, idx) => {
          const prev = visiblePages[idx - 1];
          const showEllipsis = prev && page - prev > 1;
          return (
            <React.Fragment key={page}>
              {showEllipsis && <span className="px-1 text-slate-300">…</span>}
              <button
                type="button"
                onClick={() => onPageChange(page)}
                className={`assess-mod-inventory-pagination w-9 h-9 flex items-center justify-center rounded-xl text-sm font-bold transition-all ${
                  currentPage === page
                    ? 'bg-red-600 text-white shadow-md shadow-red-100'
                    : 'bg-white border border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500'
                }`}
              >
                {page}
              </button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
            currentPage >= totalPages
              ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed'
              : 'bg-white border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500 shadow-sm'
          }`}
        >
          Next
          <FiChevronRight className="text-xs" />
        </button>
      </div>
    </div>
  );
};

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const TimePicker12Hour = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const hourRef = React.useRef(null);
  const minuteRef = React.useRef(null);
  const dropdownPanelRef = React.useRef(null);
  const [position, setPosition] = React.useState(null);

  let initialHour = 10;
  let initialMinute = 0;
  let initialAmpm = 'AM';

  if (value) {
    const parts = value.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0], 10);
      initialMinute = parseInt(parts[1], 10);
      if (isNaN(h)) h = 10;
      if (isNaN(initialMinute)) initialMinute = 0;

      initialAmpm = h >= 12 ? 'PM' : 'AM';
      initialHour = h % 12;
      if (initialHour === 0) initialHour = 12;
    }
  }

  const syncPosition = React.useCallback(() => {
    if (!dropdownRef.current) return null;
    const rect = dropdownRef.current.getBoundingClientRect();
    const width = 160; // w-[160px]
    let left = rect.left;
    const viewportTop = rect.bottom + 4;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) {
      left = 8;
    }
    return { top: viewportTop, left };
  }, []);

  React.useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const next = syncPosition();
    if (next) setPosition(next);
  }, [isOpen, syncPosition]);

  React.useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourRef.current) {
          const selected = hourRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
        if (minuteRef.current) {
          const selected = minuteRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      }, 30);
    }
  }, [isOpen]);

  const handleValChange = (newH, newM, newAmpm) => {
    let h24 = newH;
    if (newAmpm === 'PM' && h24 < 12) h24 += 12;
    if (newAmpm === 'AM' && h24 === 12) h24 = 0;

    const hStr = String(h24).padStart(2, '0');
    const mStr = String(newM).padStart(2, '0');
    onChange(`${hStr}:${mStr}`);
  };

  const formattedHour = String(initialHour).padStart(2, '0');
  const formattedMinute = String(initialMinute).padStart(2, '0');

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between bg-[#fcfdfe] border rounded-full px-3 py-1.5 cursor-pointer shadow-sm select-none transition-all w-[115px] hover:bg-slate-50 border-slate-200 focus:outline-none ${
          isOpen ? 'border-red-500 ring-2 ring-red-100' : ''
        } ${disabled ? 'opacity-80 cursor-default pointer-events-none' : ''}`}
      >
        <span className="text-xs font-bold text-slate-700 tracking-wide">
          {formattedHour}:{formattedMinute} {initialAmpm}
        </span>
        <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && position && createPortal(
        <div 
          ref={dropdownPanelRef}
          className="bg-white border border-slate-200 rounded-2xl shadow-xl p-2.5 flex flex-col gap-2 w-[160px]"
          style={{ 
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 999999
          }}
        >
          <style>{`
            .custom-timepicker-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Selected Display Row */}
          <div className="grid grid-cols-3 gap-1 pb-2 border-b border-slate-100">
            <div className="h-8 rounded border-2 border-slate-800 bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedHour}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedMinute}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {initialAmpm}
            </div>
          </div>

          {/* Picker Columns */}
          <div className="flex justify-between gap-1 h-[160px]">
            {/* Hour Column */}
            <div 
              ref={hourRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                const hVal = String(h).padStart(2, '0');
                const isSelected = h === initialHour;
                return (
                  <button
                    type="button"
                    key={h}
                    onClick={() => handleValChange(h, initialMinute, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {hVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* Minute Column */}
            <div 
              ref={minuteRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => {
                const mVal = String(m).padStart(2, '0');
                const isSelected = m === initialMinute;
                return (
                  <button
                    type="button"
                    key={m}
                    onClick={() => handleValChange(initialHour, m, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {mVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* AM/PM Column */}
            <div 
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {['AM', 'PM'].map((ampm) => {
                const isSelected = ampm === initialAmpm;
                return (
                  <button
                    type="button"
                    key={ampm}
                    onClick={() => handleValChange(initialHour, initialMinute, ampm)}
                    className={`w-full py-1.5 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {ampm}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const TestsDataTable = ({ data = [], onEdit, onDelete, refreshData, onPackDeleted, onResumeDraftClick }) => {
  const { activeModule, navigateToModule, setActiveModule, setSelectedTestId } = useAssessmentContext();
  const [activeFormId, setActiveFormId] = React.useState(null);
  const [savedPackId, setSavedPackId] = React.useState(null);
  const [inlineIsEditing, setInlineIsEditing] = React.useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [publishedPackRow, setPublishedPackRow] = React.useState(null);
  const [packStatusOverrides, setPackStatusOverrides] = React.useState({});
  const [expanded, setExpanded] = React.useState({});
  const [popupConfig, setPopupConfig] = React.useState({ isOpen: false });
  const [introVideoModal, setIntroVideoModal] = React.useState({
    isOpen: false,
    testName: '',
    videos: [],
  });
  const [sorting, setSorting] = React.useState([]);
  const [subSortConfig, setSubSortConfig] = React.useState({ key: 'id', direction: 'desc' });
  const [groupsPagination, setGroupsPagination] = React.useState({});

  const getGroupsPageIndex = React.useCallback(
    (assessmentId) => groupsPagination[String(assessmentId)] ?? 0,
    [groupsPagination]
  );

  const setGroupsPageIndex = React.useCallback((assessmentId, pageIndex) => {
    setGroupsPagination((prev) => ({
      ...prev,
      [String(assessmentId)]: Math.max(0, pageIndex),
    }));
  }, []);

  const handleExpandedChange = React.useCallback((updater) => {
    setExpanded((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      Object.keys(next).forEach((key) => {
        if (next[key] && !prev[key]) {
          setGroupsPagination((p) => ({ ...p, [key]: 0 }));
        }
      });
      return next;
    });
  }, []);

  React.useEffect(() => {
    setGroupsPagination({});
  }, [subSortConfig.key, subSortConfig.direction]);
  const dropdownRef = React.useRef(null);
  const employeeDropdownPanelRef = React.useRef(null);
  const groupRolesDropdownRef = React.useRef(null);
  const groupRolesPanelRef = React.useRef(null);
  const [templates, setTemplates] = React.useState([]);
  const [filteredEmployees, setFilteredEmployees] = React.useState([]);
  const [collegeTemplates, setCollegeTemplates] = React.useState([]);
  const [rolesList, setRolesList] = React.useState([]);
  const [formData, setFormData] = React.useState({
    pack_name: '',
    template_id: '',
    user_role: [], // Changed to array for multi-select
    scheduled_date: new Date().toISOString().split('T')[0],
    start_time: '10:00',
    end_time: '11:00',
    duration: 60,
    candidates: '',
    selectedCandidates: []
  });

  // Keep accordion open while adding/editing a group (survives silent data refresh)
  React.useEffect(() => {
    if (activeFormId != null) {
      setExpanded((prev) => ({ ...prev, [String(activeFormId)]: true }));
    }
  }, [data, activeFormId]);

  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const tRes = await testService.getTemplates();
        if (tRes.status === 'success') setTemplates(tRes.templates);
        
        const rRes = await testService.getJobRoles();
        const roles = rRes?.data ?? (Array.isArray(rRes) ? rRes : []);
        if (Array.isArray(roles)) setRolesList(roles);
      } catch (err) {
        toast.error('Failed to fetch initial data');
      }
    };
    fetchInitialData();
  }, []);

  const activeAssessment = React.useMemo(
    () => (activeFormId ? data.find(a => a.id === activeFormId) : null),
    [activeFormId, data]
  );
  const isHrFresherAssessment = isHrCategory(activeAssessment?.category, activeAssessment?.category_id);

  const formatTestType = (test) => {
    const value = (test?.assessment_type || test?.type || '').trim();
    return value || '—';
  };

  /* LEGACY: Assign To options from assessment-level assigned_to (Create New Test roles).
  React.useEffect(() => {
    ...
      const res = await testService.getUsersByRoles(activeAssessment.assigned_to);
    ...
  }, [activeFormId, activeAssessment]);
  */

  const fetchExternalSetupGroups = React.useCallback(async () => {
    try {
      const res = await testService.getHrCollegeTemplates();
      if (res.status === 'success') {
        const groups = (res.college_templates || []).map((g) => ({
          id: g.id || g.template_name || g.name,
          name: g.name || g.template_name || g.id,
          template_name: g.template_name || g.name,
          student_count: g.student_count ?? 0,
          student_ids: g.student_ids || [],
        }));
        setCollegeTemplates(groups);
      }
    } catch (err) {
      console.error('Failed to fetch External Setup groups:', err);
      toast.error('Failed to load External Setup list');
    }
  }, []);

  const [loadingCandidates, setLoadingCandidates] = React.useState(false);

  React.useEffect(() => {
    if (!activeFormId || !activeAssessment) {
      setFilteredEmployees([]);
      setCollegeTemplates([]);
      return;
    }

    if (isHrCategory(activeAssessment.category, activeAssessment.category_id)) {
      fetchExternalSetupGroups();
    } else {
      setCollegeTemplates([]);
    }
  }, [activeFormId, activeAssessment, fetchExternalSetupGroups]);

  React.useEffect(() => {
    if (!activeFormId || !activeAssessment) {
      setFilteredEmployees([]);
      return;
    }

    const roleIds = Array.isArray(formData.user_role)
      ? formData.user_role.map(String).filter(Boolean)
      : [];

    if (roleIds.length === 0) {
      setFilteredEmployees([]);
      return;
    }

    setLoadingCandidates(true);
    if (!isHrCategory(activeAssessment.category, activeAssessment.category_id)) {
      const fetchEmployees = async () => {
        try {
          const res = await testService.getUsersByRoles(roleIds.join(','));
          const employees = res?.data ?? (Array.isArray(res) ? res : []);
          setFilteredEmployees(employees || []);
        } catch (err) {
          console.error('Failed to fetch filtered employees:', err);
        } finally {
          setLoadingCandidates(false);
        }
      };
      fetchEmployees();
    } else {
      // External: Fetch students for selected college templates in user_role
      Promise.all(
        roleIds.map(roleName => 
          testService.getStudentsByCollege(roleName)
            .then(res => res.students || [])
            .catch(() => [])
        )
      ).then(results => {
        const allStudents = results.flat();
        const uniqueStudents = [];
        const seenIds = new Set();
        allStudents.forEach(s => {
          const uniqueId = String(s.id || s.username || s.email || '');
          if (uniqueId && !seenIds.has(uniqueId)) {
            seenIds.add(uniqueId);
            uniqueStudents.push({
              id: s.id,
              name: s.student_name || s.name || '',
              email: s.email || s.username || '',
              job_role: s.college_name || '',
            });
          }
        });
        setFilteredEmployees(uniqueStudents);
      })
      .catch(e => console.error(e))
      .finally(() => setLoadingCandidates(false));
    }
  }, [activeFormId, activeAssessment, formData.user_role]);

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let diff = (eH * 60 + eM) - (sH * 60 + sM);
    if (diff < 0) diff += 1440; // Handle overnight durations
    return diff;
  };

  const handleTimeChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    const newDuration = calculateDuration(newFormData.start_time, newFormData.end_time);
    setFormData({ ...newFormData, duration: newDuration });
  };

  const resetInlineForm = () => {
    setFormData({
      pack_name: '',
      template_id: '',
      user_role: [],
      scheduled_date: new Date().toISOString().split('T')[0],
      start_time: '10:00',
      end_time: '11:00',
      duration: 60,
      candidates: '',
      selectedCandidates: []
    });
    setSavedPackId(null);
    setInlineIsEditing(true);
    setIsPreviewOpen(false);
    setIsEmployeeDropdownOpen(false);
    setIsGroupRolesOpen(false);
    setGroupRolesSearchTerm('');
  };

  const buildOptimisticPack = (packId, assessmentId) => {
    const template = templates.find((t) => String(t.id) === String(formData.template_id));
    return {
      id: packId,
      pack_name: formData.pack_name,
      template_id: formData.template_id,
      template_name: template?.name || '',
      scheduled_date: formData.scheduled_date,
      start_time: formData.start_time,
      end_time: formData.end_time,
      duration: formData.duration,
      candidates: formData.candidates,
      user_role: Array.isArray(formData.user_role) ? formData.user_role.join(',') : (formData.user_role || ''),
      status: 'published',
      assessment_id: assessmentId,
    };
  };

  const resolvePackForRow = (row, packId, fallbackPack) => {
    const fromApi = row.original.test_packs?.find((p) => String(p.id) === String(packId));
    const pack = fromApi || fallbackPack;
    if (!pack) return null;
    const statusOverride = packStatusOverrides[String(packId)];
    return statusOverride ? { ...pack, status: statusOverride } : pack;
  };

  const openInlineGroupForm = (assessmentId) => {
    const idKey = String(assessmentId);
    setPublishedPackRow(null);
    setExpanded((prev) => ({ ...prev, [idKey]: true }));
    if (activeFormId === assessmentId) {
      setActiveFormId(null);
      resetInlineForm();
      return;
    }
    resetInlineForm();
    setActiveFormId(assessmentId);
  };

  const handleSaveInline = async (assessmentId) => {
    if (!formData.pack_name.trim()) return toast.error('Please enter a group name');
    if (!formData.template_id) return toast.error('Please select a template for this group');
    const assessment = data.find((a) => a.id === assessmentId);
    const isHr = isHrCategory(assessment?.category, assessment?.category_id);
    if (!Array.isArray(formData.user_role) || formData.user_role.length === 0) {
      return toast.error(isHr ? 'Please select at least one college template' : 'Please select at least one assigned role');
    }
    if (!formData.candidates || formData.selectedCandidates.length === 0) {
      return toast.error(isHr ? 'Please assign at least one candidate student' : 'Please assign at least one member');
    }

    // Local (wizard-created) assessment: save group to localStorage, not the backend.
    // The backend requires a numeric integer assessment_id — local IDs are strings like 'pub-TST-XXXX'.
    if (assessment?.isLocal) {
      try {
        const pubStr = localStorage.getItem('published_tests');
        if (!pubStr) throw new Error('Local assessment data not found');
        const parsed = JSON.parse(pubStr);
        const testIndex = parsed.findIndex(t => 'pub-' + t.id === assessmentId || String(t.id) === assessmentId || String(t.backendId) === String(assessmentId));
        if (testIndex === -1) throw new Error('Local assessment not found in storage');

        const matchedTpl = templates.find(t => String(t.id) === String(formData.template_id));
        const newGroup = {
          id: 'group-' + Date.now(),
          pack_name: formData.pack_name,
          template_id: formData.template_id,
          template_name: matchedTpl?.name || matchedTpl?.template_name || '',
          user_role: Array.isArray(formData.user_role) ? formData.user_role.join(',') : (formData.user_role || ''),
          candidates: formData.candidates || '',
          scheduled_date: formData.scheduled_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          duration: formData.duration || 60,
          status: 'Draft',
        };
        parsed[testIndex].groups = [...(parsed[testIndex].groups || []), newGroup];
        localStorage.setItem('published_tests', JSON.stringify(parsed));
        window.dispatchEvent(new Event('draft-updated'));

        setSavedPackId(newGroup.id);
        setInlineIsEditing(false);
        toast.success('Group saved successfully');
        refreshData?.();
      } catch (err) {
        console.error(err);
        toast.error(err?.message || 'Failed to save group');
      }
      return;
    }

    try {
      const res = await testService.saveTestPack({
        assessment_id: assessmentId,
        ...formData
      });
      if (res.status === 'success') {
        setSavedPackId(res.id);
        setInlineIsEditing(false);
        setExpanded((prev) => ({ ...prev, [String(assessmentId)]: true }));
        toast.success('Group saved successfully');
        await refreshData?.();
        setExpanded((prev) => ({ ...prev, [String(assessmentId)]: true }));
      } else {
        toast.error(res.message || 'Failed to save group');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save group');
    }
  };

  const handleToggleInlineEdit = async (assessmentId) => {
    if (!savedPackId) return;
    if (inlineIsEditing) {
      if (!formData.pack_name.trim()) return toast.error('Please enter a group name');
      if (!formData.template_id) return toast.error('Please select a template for this group');
      const assessment = data.find((a) => a.id === assessmentId);
      const isHr = isHrCategory(assessment?.category, assessment?.category_id);
      if (!Array.isArray(formData.user_role) || formData.user_role.length === 0) {
        return toast.error(isHr ? 'Please select at least one college template' : 'Please select at least one assigned role');
      }
      if (!formData.candidates || formData.selectedCandidates.length === 0) {
        return toast.error(isHr ? 'Please assign at least one candidate student' : 'Please assign at least one member');
      }
      try {
        const res = await testService.updateTestPack(savedPackId, {
          assessment_id: assessmentId,
          ...formData,
        });
        if (res.status === 'success') {
          toast.success('Group updated successfully');
          setInlineIsEditing(false);
          await refreshData?.();
          setExpanded((prev) => ({ ...prev, [String(assessmentId)]: true }));
        } else {
          toast.error(res.message || 'Failed to update group');
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to update group');
      }
    } else {
      setInlineIsEditing(true);
    }
  };

  const handlePackDeletedLocally = React.useCallback(
    (assessmentId, packId) => {
      setPublishedPackRow((prev) =>
        prev?.assessmentId === assessmentId && String(prev.packId) === String(packId) ? null : prev
      );
      setPackStatusOverrides((prev) => {
        const key = `${assessmentId}-${packId}`;
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      onPackDeleted?.(assessmentId, packId);
    },
    [onPackDeleted]
  );

  const handleDeleteInline = () => {
    if (!savedPackId) {
      setActiveFormId(null);
      resetInlineForm();
      return;
    }
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this group?',
      message: 'Are you sure you want to delete this group? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        // Local group: remove from localStorage
        if (String(savedPackId).startsWith('group-')) {
          const pubStr = localStorage.getItem('published_tests');
          if (pubStr) {
            try {
              const parsed = JSON.parse(pubStr);
              const testIndex = parsed.findIndex(t =>
                'pub-' + t.id === activeFormId || String(t.id) === activeFormId || String(t.backendId) === String(activeFormId)
              );
              if (testIndex > -1) {
                parsed[testIndex].groups = (parsed[testIndex].groups || []).filter(
                  g => String(g.id) !== String(savedPackId)
                );
                localStorage.setItem('published_tests', JSON.stringify(parsed));
                window.dispatchEvent(new Event('draft-updated'));
              }
            } catch (e) { console.error(e); }
          }
          setPopupConfig({ isOpen: false });
          toast.success('Group deleted successfully');
          setActiveFormId(null);
          resetInlineForm();
          return;
        }
        try {
          const res = await testService.deleteTestPack(savedPackId);
          if (res.status === 'success') {
            const assessmentId = activeFormId;
            const packId = savedPackId;
            setPopupConfig({ isOpen: false });
            toast.success('Group deleted successfully');
            if (assessmentId && packId) {
              handlePackDeletedLocally(assessmentId, packId);
            }
            setActiveFormId(null);
            resetInlineForm();
          } else {
            toast.error(res.message || 'Failed to delete group');
            setPopupConfig({ isOpen: false });
          }
        } catch (err) {
          console.error(err);
          toast.error('Failed to delete group');
          setPopupConfig({ isOpen: false });
        }
      },
    });
  };

  const handlePublishInline = (packId, assessmentId) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Publish this group?',
      message: 'This will make the test visible to assigned candidates.',
      confirmText: 'Yes, publish it!',
      onConfirm: async () => {
        // Local group (saved only in localStorage): persist to backend first
        if (String(packId).startsWith('group-')) {
          const assessment = data.find(a => a.id === assessmentId);
          try {
            // Step 1: Create backend assessment if needed
            let backendAssessmentId = assessment?.backendId || (!assessment?.isLocal ? assessment?.id : null);
            if (!backendAssessmentId) {
              const rawId = String(assessmentId || '').replace(/^pub-/, '');
              const isVideoOn = assessment?.add_video === 'Yes' || assessment?.add_video === true || assessment?.videoEnabled === true;
              const introVideosVal = assessment?.intro_videos || assessment?.videoFiles || [];

              const createRes = await testService.createTest({
                name: assessment?.name || 'Assessment',
                category: assessment?.category || 'Internal',
                code: rawId || ('TST-' + Date.now()),
                assessment_type: assessment?.type || assessment?.assessment_type || '',
                instructions: assessment?.instructions || '',
                description: '',
                pass_mark: 50,
                attempts: 1,
                proctored_exam: assessment?.proctored_exam ?? (assessment?.fullscreenMode ? 1 : 0),
                browser_lockdown: assessment?.browser_lockdown ?? (assessment?.tabSwitchWarning ? 1 : 0),
                allow_backtracking: assessment?.allow_backtracking ?? (assessment?.navigationControl ? 1 : 0),
                show_results: assessment?.show_results ?? (assessment?.autoSubmit ? 1 : 0),
                add_video: isVideoOn ? 1 : 0,
                intro_videos: introVideosVal.filter(v => typeof v === 'string' && (v.startsWith('/') || v.includes('/')))
              });
              if (createRes.status !== 'success') throw new Error(createRes.message || 'Failed to save assessment');
              backendAssessmentId = createRes.id;
              // Persist backendId to localStorage
              const pubStr = localStorage.getItem('published_tests');
              if (pubStr) {
                try {
                  const parsed = JSON.parse(pubStr);
                  const ti = parsed.findIndex(t => 'pub-' + t.id === assessmentId || String(t.id) === assessmentId || String(t.backendId) === String(assessmentId));
                  if (ti > -1) { parsed[ti].backendId = backendAssessmentId; localStorage.setItem('published_tests', JSON.stringify(parsed)); }
                } catch(e) { console.error(e); }
              }
            }

            // Step 1b: Upload intro videos if present
            const videoFilesMeta = assessment?.videoFiles || [];
            if (videoFilesMeta.length > 0) {
              try {
                const filesToUpload = [];
                for (const fileObj of videoFilesMeta) {
                  const name = fileObj && typeof fileObj === 'object' ? fileObj.name : fileObj;
                  const cachedFile = window.selectedVideoFiles?.[name];
                  if (cachedFile) {
                    filesToUpload.push(cachedFile);
                  }
                }
                if (filesToUpload.length > 0) {
                  const uploadRes = await testService.uploadIntroVideos(backendAssessmentId, filesToUpload);
                  if (uploadRes.status !== 'success') {
                    throw new Error(uploadRes.message || 'Failed to upload intro videos');
                  }
                }
              } catch (uploadErr) {
                console.error('Failed to upload intro videos during publishing:', uploadErr);
                throw uploadErr;
              }
            }

            // Step 2: Save pack to backend
            const savePackRes = await testService.saveTestPack({
              assessment_id: backendAssessmentId,
              pack_name: formData.pack_name,
              template_id: formData.template_id || assessment?.template?.id || null,
              user_role: Array.isArray(formData.user_role) ? formData.user_role.join(',') : (formData.user_role || ''),
              candidates: formData.candidates || '',
              scheduled_date: formData.scheduled_date,
              start_time: formData.start_time,
              end_time: formData.end_time,
              duration: formData.duration || 60,
              status: 'Draft',
            });
            if (savePackRes.status !== 'success') throw new Error(savePackRes.message || 'Failed to save group');
            const backendPackId = savePackRes.id;
            // Step 3: Publish via backend
            const publishRes = await testService.publishTestPack(backendPackId);
            if (publishRes.status !== 'success') throw new Error(publishRes.message || 'Failed to publish group');
            // Step 4: Update localStorage group status
            const pubStr2 = localStorage.getItem('published_tests');
            if (pubStr2) {
              try {
                const parsed2 = JSON.parse(pubStr2);
                const ti2 = parsed2.findIndex(t => 'pub-' + t.id === assessmentId || String(t.id) === assessmentId || String(t.backendId) === String(assessmentId));
                if (ti2 > -1) {
                  parsed2[ti2].groups = (parsed2[ti2].groups || []).map(g =>
                    String(g.id) === String(packId) ? { ...g, status: 'Published', backendPackId } : g
                  );
                  localStorage.setItem('published_tests', JSON.stringify(parsed2));
                  window.dispatchEvent(new Event('draft-updated'));
                }
              } catch(e) { console.error(e); }
            }
            setActiveFormId(null);
            resetInlineForm();
            setPopupConfig({ isOpen: false });
            toast.success('Group published successfully');
            await refreshData?.();
          } catch (err) {
            console.error(err);
            setPopupConfig({ isOpen: false });
            toast.error(err?.response?.data?.message || err?.message || 'Failed to publish group');
          }
          return;
        }
        // DB-backed group: standard flow
        try {
          const res = await testService.publishTestPack(packId);
          if (res.status === 'success') {
            const optimisticPack = buildOptimisticPack(packId, assessmentId);
            setPackStatusOverrides((prev) => ({ ...prev, [String(packId)]: 'published' }));
            setPublishedPackRow({
              assessmentId,
              packId,
              optimisticPack,
            });
            setActiveFormId(null);
            resetInlineForm();
            setExpanded((prev) => ({ ...prev, [String(assessmentId)]: true }));
            toast.success('Group published successfully');
            await refreshData?.();
          } else {
            toast.error(res.message || 'Failed to publish group');
          }
        } catch (err) {
          console.error(err);
          toast.error('Failed to publish group');
        } finally {
          setPopupConfig({ isOpen: false });
        }
      }
    });
  };

  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = React.useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = React.useState('');
  const [isGroupRolesOpen, setIsGroupRolesOpen] = React.useState(false);
  const [groupRolesSearchTerm, setGroupRolesSearchTerm] = React.useState('');

  const getRolesForForm = () => {
    if (isHrFresherAssessment) {
      return collegeTemplates.map(t => ({
        id: t.id,
        name: t.name
      }));
    }
    return rolesList;
  };

  const resolveRoleNames = (roleIds) => {
    if (!roleIds) return [];
    const ids = Array.isArray(roleIds)
      ? roleIds.map(String)
      : String(roleIds).split(',').map((s) => s.trim()).filter(Boolean);
    const source = getRolesForForm();
    return ids.map((id) => {
      const found = source.find((r) => String(r.id) === id);
      return found ? found.name : id;
    });
  };

  const visibleGroupRoles = getRolesForForm().filter((role) =>
    role.name.toLowerCase().includes(groupRolesSearchTerm.toLowerCase())
  );

  const getGroupColumnClass = (key) => {
    if (key === 'user_role') return 'assess-mod-groups-col-roles';
    if (key === 'candidates') return 'assess-mod-groups-col-assign';
    if (key === 'pack_name') return 'assess-mod-groups-col-name';
    if (key === 'template_name') return 'assess-mod-groups-col-template';
    if (key === 'scheduled_date') return 'assess-mod-groups-col-date';
    if (key === 'start_time') return 'assess-mod-groups-col-start';
    if (key === 'end_time') return 'assess-mod-groups-col-end';
    if (key === 'duration') return 'assess-mod-groups-col-duration';
    return '';
  };

  const isGroupScheduleColumn = (key) =>
    ['scheduled_date', 'start_time', 'end_time', 'duration'].includes(key);

  const groupDatetimeInputClass =
    'assess-mod-groups-datetime-input w-full outline-none transition-all shadow-sm focus:ring-4 focus:ring-rose-50';

  const toggleGroupRole = (roleId) => {
    const id = String(roleId);
    setFormData((prev) => {
      const current = (prev.user_role || []).map(String);
      const nextRoles = current.includes(id)
        ? current.filter((r) => r !== id)
        : [...current, id];
      return {
        ...prev,
        user_role: nextRoles,
        selectedCandidates: [],
        candidates: '',
      };
    });
  };

  const selectAllGroupRoles = (checked) => {
    const visibleIds = visibleGroupRoles.map((r) => String(r.id));
    setFormData((prev) => {
      const current = (prev.user_role || []).map(String);
      const nextRoles = checked
        ? [...new Set([...current, ...visibleIds])]
        : current.filter((id) => !visibleIds.includes(id));
      return {
        ...prev,
        user_role: nextRoles,
        selectedCandidates: [],
        candidates: '',
      };
    });
  };

  // Handle click outside for employee dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current?.contains(event.target);
      const inPanel = employeeDropdownPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsEmployeeDropdownOpen(false);
      }
    };

    if (isEmployeeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    if (!isEmployeeDropdownOpen) {
      setEmployeeSearchTerm('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEmployeeDropdownOpen]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = groupRolesDropdownRef.current?.contains(event.target);
      const inPanel = groupRolesPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsGroupRolesOpen(false);
      }
    };
    if (isGroupRolesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setGroupRolesSearchTerm('');
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isGroupRolesOpen]);

  const toggleEmployee = (empId) => {
    setFormData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates : [];
      const updated = current.includes(String(empId))
        ? current.filter(id => id !== String(empId))
        : [...current, String(empId)];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
    });
  };

  const toggleCollegeTemplate = (template) => {
    const ids = (template.student_ids || []).map(String);
    const templateIdStr = String(template.id);
    setFormData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates.map(String) : [];
      const allSelected = ids.length > 0 && ids.every(id => current.includes(id));
      const updated = allSelected
        ? current.filter(id => !ids.includes(id))
        : [...new Set([...current, ...ids])];
      const currentRoles = Array.isArray(prev.user_role) ? prev.user_role.map(String) : [];
      const updatedRoles = allSelected
        ? currentRoles.filter(r => r !== templateIdStr)
        : [...new Set([...currentRoles, templateIdStr])];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(','), user_role: updatedRoles };
    });
  };

  const isCollegeTemplateSelected = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(formData.selectedCandidates) ? formData.selectedCandidates.map(String) : [];
    return ids.length > 0 && ids.every(id => current.includes(id));
  };

  const getVisibleCollegeTemplates = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  const handlePreview = (batch, assessmentId) => {
    setSelectedTestId(assessmentId);
    setActiveModule('evaluation');
  };

  const handleOpenIntroVideo = React.useCallback((test) => {
    const rawVideos = normalizeIntroVideosList(test?.intro_videos);
    if (!rawVideos.length) {
      toast.error('No intro video uploaded for this test.');
      return;
    }
    setIntroVideoModal({
      isOpen: true,
      testName: test?.name || 'Test',
      videos: rawVideos,
    });
  }, []);

  const handleCloseIntroVideo = React.useCallback(() => {
    setIntroVideoModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Test Name',
        cell: ({ row }) => {
          const isExpanded = row.getIsExpanded();
          const introVideos = normalizeIntroVideosList(row.original.intro_videos);
          const hasIntroVideo =
            isAssessmentFlagOn(row.original.add_video) && introVideos.length > 0;
          return (
            <div className="flex items-center gap-4">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 border ${
                  isExpanded 
                    ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-100 rotate-180' 
                    : 'bg-white border-red-50 text-red-400 group-hover:bg-red-50 group-hover:border-red-200 group-hover:text-red-600'
                }`}
              >
                <FiChevronDown className={`text-sm transition-all ${isExpanded ? 'stroke-[3px]' : 'stroke-[2px]'}`} />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-50 text-red-600 rounded-lg flex items-center justify-center font-bold text-xs border border-red-100">
                  {row.original.name ? row.original.name.charAt(0) : 'T'}
                </div>
                <div>
                  <div className="assess-mod-inventory-test-name flex items-center gap-2 flex-wrap">
                    <span>{row.original.name}</span>
                    {row.original.isLocalDraft && (
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          onResumeDraftClick?.(row.original);
                        }}
                        className="text-[9px] font-black px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100 font-bold animate-pulse cursor-pointer hover:bg-rose-100 transition-colors uppercase tracking-wider"
                        title="Click to resume draft"
                      >
                        DRAFT
                      </span>
                    )}
                    {hasIntroVideo && (
                      <button
                        type="button"
                        className="assess-mod-inventory-video-indicator"
                        title="Play intro video"
                        aria-label={`Play intro video for ${row.original.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenIntroVideo(row.original);
                        }}
                      >
                        <FiVideo className="text-xs" />
                      </button>
                    )}
                  </div>
                  <div className="assess-mod-inventory-test-meta">Created on {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString() : new Date().toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => {
          const category = row.original.category || '';
          const label =
            category === 'Enova'
              ? 'Internal'
              : category === 'HR Recruitment-Fresher'
                ? 'External'
                : category || 'General';
          const isInternal = label === 'Internal';
          const isExternal = label === 'External';
          return (
            <div className="text-center">
              <span
                className={`assess-mod-inventory-chip ${
                  isInternal
                    ? 'assess-mod-inventory-chip--internal'
                    : isExternal
                      ? 'assess-mod-inventory-chip--external'
                      : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}
              >
                {label}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <div className="text-center">
            <span className="text-sm font-bold text-slate-600">{formatTestType(row.original)}</span>
          </div>
        ),
      },
      {
        id: 'batches',
        header: 'Groups',
        cell: ({ row }) => {
          const packs = row.original.test_packs || [];
          const completed = row.original.completed_count || 0;
          return (
            <div className="flex flex-col items-center">
              <span className="text-sm font-bold text-slate-700">
                {packs.length}{' '}
                <span className="text-slate-400 text-xs font-semibold uppercase">Groups</span>
              </span>
              <span
                className={`text-xs font-bold uppercase tracking-wide ${
                  completed > 0 ? 'text-emerald-600' : 'text-slate-400'
                }`}
              >
                {completed} Completed
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const pendingEvalCount = getEffectivePendingEvalCount(row.original);
          const isLocal = row.original.isLocal;
          const isLocalPublished = row.original.isLocalPublished;
          const isDraft = row.original.status === 'Draft';
          return (
          <div className="flex items-center justify-center gap-4 px-6">
            {(!isLocal || isLocalPublished) && (
              <button 
                className={`assess-mod-inventory-action-btn assess-mod-inventory-action-btn--red w-9 h-9 rounded-xl flex items-center justify-center transition-all group shadow-sm ${
                  pendingEvalCount > 0 ? 'evaluation-blink-button' : ''
                }`}
                title="Result & Evaluation"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setSelectedTestId(row.original.id);
                  setActiveModule('evaluation');
                }}
              >
                <FiFileText className="text-base group-hover:scale-110 transition-transform" />
              </button>
            )}
            {(!isLocal || isDraft || isLocalPublished) && (
              <button 
                onClick={(e) => { e.stopPropagation(); onEdit(row.original); }}
                className="assess-mod-inventory-action-btn assess-mod-inventory-action-btn--blue w-9 h-9 rounded-xl flex items-center justify-center transition-all group shadow-sm"
                title="Edit Assessment"
              >
                <AssessmentEditIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            )}
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete?.(row.original); }}
              className="assess-mod-inventory-action-btn assess-mod-inventory-action-btn--red w-9 h-9 rounded-xl flex items-center justify-center transition-all group shadow-sm"
              title="Delete Assessment"
            >
              <FiTrash2 className="text-base group-hover:scale-110 transition-transform" />
            </button>
          </div>
          );
        },
      },
    ],
    [onEdit, onDelete, rolesList, setActiveModule, formatTestType, handleOpenIntroVideo, onResumeDraftClick]
  );

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onExpandedChange: handleExpandedChange,
    state: {
      sorting,
      expanded,
    },
    getRowCanExpand: () => true,
    initialState: {
      pagination: {
        pageSize: 7,
      },
    },
  });

  return (
    <div className="w-full assess-mod-inventory-table-wrap assess-mod-table-wrap overflow-x-auto">
      <table className="w-full text-left min-w-[880px]" style={{ width: '100%' }}>
        <thead className="bg-white border-b border-slate-100">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <th 
                  key={header.id} 
                  className={`px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wide ${index === 0 ? '' : 'text-center'} ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className={`flex items-center gap-2 ${index === 0 ? 'justify-start' : 'justify-center'}`}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <div className="flex flex-col text-[8px] text-slate-300">
                        <i className={`bi bi-caret-up-fill leading-none ${header.column.getIsSorted() === 'asc' ? 'text-red-500' : ''}`}></i>
                        <i className={`bi bi-caret-down-fill leading-none ${header.column.getIsSorted() === 'desc' ? 'text-red-500' : ''}`}></i>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr 
                onClick={row.getToggleExpandedHandler()}
                className={`cursor-pointer transition-all duration-200 group ${row.getIsExpanded() ? 'bg-slate-50/80 border-l-4 border-l-red-500' : 'hover:bg-[#f8fafc]'} border-b border-[#f1f5f9]`}
              >
                {row.getVisibleCells().map(cell => (
                  <td 
                    key={cell.id} 
                    className="bg-transparent align-middle min-h-[76px] py-4 px-6"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <tr>
                  <td colSpan={columns.length} className="p-0 border-b border-slate-100 align-top">
                    <div className="assess-mod-groups-accordion bg-slate-50/50 shadow-inner max-w-full overflow-hidden">
                      <div className="assess-mod-groups-accordion-card bg-white border border-slate-100 overflow-hidden shadow-xl shadow-slate-200/50 max-w-full">
                        <div className="assess-mod-groups-accordion-toolbar flex flex-wrap items-center justify-between gap-2 px-6 py-3 bg-white border-b border-slate-50">
                          <div className="flex flex-wrap items-center gap-2">
                            <h6 className="text-xs font-black uppercase tracking-wide text-[#1e293b] mb-0 shrink-0">
                              Groups
                            </h6>
                          </div>
                            <button
                              type="button"
                              className="btn-assessment btn-assessment-sm btn-assessment-danger shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                openInlineGroupForm(row.original.id);
                              }}
                            >
                              <FiPlus className="text-[10px]" /> Add New Group
                            </button>
                        </div>
                        <div className="assess-mod-groups-panel-scroll">
                          <div className="assess-mod-groups-panel">
                          {(() => {
                            const isHrGroupRow = isHrCategory(row.original.category, row.original.category_id);
                            const rawPacks = row.original.isLocal ? (row.original.groups || []) : (row.original.test_packs || []);
                            const sortedPacks = row.original.isLocal ? rawPacks : sortAssessmentPacks(
                              filterAssessmentPacks(
                                rawPacks,
                                row,
                                activeFormId,
                                savedPackId,
                                publishedPackRow
                              ),
                              subSortConfig
                            );
                            const totalGroupPages = Math.max(
                              1,
                              Math.ceil(sortedPacks.length / GROUPS_PAGE_SIZE)
                            );
                            const rawGroupPage = getGroupsPageIndex(row.original.id);
                            const safeGroupPage = Math.min(rawGroupPage, totalGroupPages - 1);
                            const paginatedPacks = sortedPacks.slice(
                              safeGroupPage * GROUPS_PAGE_SIZE,
                              safeGroupPage * GROUPS_PAGE_SIZE + GROUPS_PAGE_SIZE
                            );
                            const groupTableColumns = isHrGroupRow ? [
                              { label: 'GROUP NAME', key: 'pack_name' },
                              { label: 'SELECT TEMPLATE', key: 'template_name' },
                              { label: 'ASSIGN TO', key: 'candidates' },
                              { label: 'DATE', key: 'scheduled_date' },
                              { label: 'START TIME', key: 'start_time' },
                              { label: 'END TIME', key: 'end_time' },
                              { label: 'DURATION', key: 'duration' },
                            ] : [
                              { label: 'GROUP NAME', key: 'pack_name' },
                              { label: 'SELECT TEMPLATE', key: 'template_name' },
                              { label: 'ASSIGNED ROLES', key: 'user_role' },
                              { label: 'ASSIGN TO', key: 'candidates' },
                              { label: 'DATE', key: 'scheduled_date' },
                              { label: 'START TIME', key: 'start_time' },
                              { label: 'END TIME', key: 'end_time' },
                              { label: 'DURATION', key: 'duration' },
                            ];
                            const groupColSpan = groupTableColumns.length + 1;

                            return (
                              <>
                          <table className="w-full text-left border-separate border-spacing-y-2 assess-mod-groups-accordion-table">
                            <thead>
                              <tr>
                                {groupTableColumns.map((col, idx) => {
                                  const key = col.key;
                                  const isSorted = subSortConfig.key === key;
                                  return (
                                    <th 
                                      key={key}
                                      className={`assess-mod-groups-th py-3 text-xs font-bold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-red-500 transition-colors ${getGroupColumnClass(key)} ${isGroupScheduleColumn(key) || idx > 2 ? 'text-center' : ''}`}
                                      onClick={() => {
                                        setSubSortConfig(prev => ({
                                          key,
                                          direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
                                        }));
                                      }}
                                    >
                                      <div className={`flex items-center gap-1.5 ${idx > 2 ? 'justify-center' : ''}`}>
                                        {col.label}
                                        {/* Removed Template Builder shortcut (+) button from SELECT TEMPLATE header */}
                                        {isHrGroupRow && col.key === 'candidates' && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigateToModule('hrUpload', 'inventory');
                                            }}
                                            className="shrink-0 w-5 h-5 bg-[#b91c1c] text-white rounded-md flex items-center justify-center hover:bg-[#991b1b] transition-colors shadow-sm"
                                            title="External Setup Candidates Onboarding & Repository"
                                            aria-label="External Setup Candidates Onboarding & Repository"
                                          >
                                            <FiPlus className="text-[9px] stroke-[2.5]" />
                                          </button>
                                        )}
                                        <div className="flex flex-col text-[7px]">
                                          <i className={`bi bi-caret-up-fill leading-none ${isSorted && subSortConfig.direction === 'asc' ? 'text-red-500' : 'text-slate-200'}`}></i>
                                          <i className={`bi bi-caret-down-fill leading-none ${isSorted && subSortConfig.direction === 'desc' ? 'text-red-500' : 'text-slate-200'}`}></i>
                                        </div>
                                      </div>
                                    </th>
                                  );
                                })}
                                <th className="assess-mod-groups-th assess-mod-groups-col-action py-3 text-xs font-bold text-slate-500 uppercase tracking-wide text-right">
                                  ACTION
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {publishedPackRow?.assessmentId === row.original.id && (() => {
                                const publishedBatch = resolvePackForRow(
                                  row,
                                  publishedPackRow.packId,
                                  publishedPackRow.optimisticPack
                                );
                                if (!publishedBatch) return null;
                                return (
                                  <BatchRow
                                    key={`published-${publishedBatch.id}`}
                                    batch={publishedBatch}
                                    assessmentId={row.original.id}
                                    onRefresh={refreshData}
                                    onPackDeleted={handlePackDeletedLocally}
                                    assignedRoles={publishedBatch.user_role || ''}
                                    rolesList={rolesList}
                                    assessmentCategory={row.original.category}
                                    templates={templates}
                                    onPreview={(b) => handlePreview(b, row.original.id)}
                                    assessment={row.original}
                                  />
                                );
                              })()}
                              {activeFormId === row.original.id && publishedPackRow?.assessmentId !== row.original.id && (() => {
                                const inlineLocked = savedPackId && !inlineIsEditing;
                                return (
                                <tr className="bg-white border border-[#f8fafc] shadow-sm rounded-2xl overflow-hidden transition-all duration-300">
                                  <td className="px-6 py-5 assess-mod-groups-col-name align-middle">
                                    <input 
                                      type="text"
                                      placeholder="e.g. Morning Group"
                                      readOnly={inlineLocked}
                                      className={`w-full bg-[#fcfdfe] border rounded-xl px-4 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${inlineLocked ? 'border-slate-100 cursor-default' : 'border-red-400 focus:ring-4 focus:ring-rose-50'}`}
                                      value={formData.pack_name}
                                      onChange={(e) => setFormData({...formData, pack_name: e.target.value})}
                                    />
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-template align-middle">
                                    <select 
                                      disabled={inlineLocked}
                                      className={`w-full bg-[#fcfdfe] border border-slate-100 rounded-xl px-4 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all appearance-none shadow-sm ${inlineLocked ? 'opacity-80 cursor-default' : 'focus:ring-4 focus:ring-rose-50'}`}
                                      value={formData.template_id}
                                      onChange={(e) => setFormData({...formData, template_id: e.target.value})}
                                    >
                                      <option value="">Select Template</option>
                                      {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  {!isHrGroupRow && (
                                    <td className="px-6 py-5 assess-mod-groups-col-roles align-middle">
                                        <div className="assess-mod-groups-picker-wrap" ref={groupRolesDropdownRef}>
                                          <GroupsInlineSearchField
                                            value={groupRolesSearchTerm}
                                            onChange={(e) => {
                                              setGroupRolesSearchTerm(e.target.value);
                                              setIsGroupRolesOpen(true);
                                            }}
                                            onFocus={() => !inlineLocked && setIsGroupRolesOpen(true)}
                                            onBlur={() => {
                                              if (!groupRolesSearchTerm) return;
                                              window.setTimeout(() => {
                                                if (!groupRolesDropdownRef.current?.contains(document.activeElement)) {
                                                  setGroupRolesSearchTerm('');
                                                }
                                              }, 150);
                                            }}
                                            placeholder={isHrGroupRow ? "Search colleges..." : "Search roles..."}
                                            disabled={inlineLocked}
                                            dropdownOpen={isGroupRolesOpen}
                                            onToggleDropdown={() => {
                                              setIsGroupRolesOpen((open) => {
                                                const next = !open;
                                                if (!next) setGroupRolesSearchTerm('');
                                                return next;
                                              });
                                            }}
                                            selectionSummary={
                                              Array.isArray(formData.user_role) && formData.user_role.length > 0 ? (
                                                <RoleSelectionSummary names={resolveRoleNames(formData.user_role)} />
                                              ) : null
                                            }
                                            toggleAriaLabel="Toggle roles list"
                                          />
                                          <GroupsPickerDropdownPortal
                                            ref={groupRolesPanelRef}
                                            isOpen={isGroupRolesOpen}
                                            anchorRef={groupRolesDropdownRef}
                                            minWidth={288}
                                            title={isHrGroupRow ? "Assigned Colleges" : "Assigned Roles"}
                                            subtitle={isHrGroupRow ? "College List" : "Role List"}
                                            count={Array.isArray(formData.user_role) ? formData.user_role.length : 0}
                                          >
                                              <div className="assess-mod-groups-member-panel__list">
                                                <GroupsPickerMemberRow
                                                  isSelectAll
                                                  selectAllLabel="Select All"
                                                  checked={
                                                    visibleGroupRoles.length > 0 &&
                                                    visibleGroupRoles.every((r) =>
                                                      (formData.user_role || []).map(String).includes(String(r.id))
                                                    )
                                                  }
                                                  onChange={(e) => selectAllGroupRoles(e.target.checked)}
                                                />
                                                {visibleGroupRoles.map((role) => (
                                                  <GroupsPickerMemberRow
                                                    key={role.id}
                                                    name={role.name}
                                                    checked={(formData.user_role || []).map(String).includes(String(role.id))}
                                                    onChange={() => toggleGroupRole(role.id)}
                                                  />
                                                ))}
                                              </div>
                                              {visibleGroupRoles.length === 0 && (
                                                <p className="assess-mod-groups-member-panel__empty">No roles found</p>
                                              )}
                                          </GroupsPickerDropdownPortal>
                                        </div>
                                    </td>
                                  )}
                                  <td className="px-6 py-5 assess-mod-groups-col-assign align-middle">
                                    <div className="assess-mod-groups-picker-wrap" ref={dropdownRef}>
                                      <GroupsInlineSearchField
                                        value={employeeSearchTerm}
                                        onChange={(e) => {
                                          setEmployeeSearchTerm(e.target.value);
                                          setIsEmployeeDropdownOpen(true);
                                        }}
                                        onFocus={async () => {
                                          if (inlineLocked) return;
                                          if (!isHrGroupRow && (!formData.user_role || formData.user_role.length === 0)) {
                                            return;
                                          }
                                          setIsEmployeeDropdownOpen(true);
                                        }}
                                        placeholder={
                                          isHrGroupRow
                                            ? 'Search candidates...'
                                            : (!formData.user_role || formData.user_role.length === 0
                                                ? 'Select roles first'
                                                : 'Search candidates...')
                                        }
                                        disabled={
                                          inlineLocked ||
                                          (!isHrGroupRow && (!Array.isArray(formData.user_role) || formData.user_role.length === 0))
                                        }
                                        dropdownOpen={isEmployeeDropdownOpen}
                                        onToggleDropdown={async () => {
                                          if (inlineLocked) return;
                                          if (!isHrGroupRow && (!formData.user_role || formData.user_role.length === 0)) {
                                            toast.error('Select assigned roles first');
                                            return;
                                          }
                                          setIsEmployeeDropdownOpen((open) => !open);
                                        }}
                                        selectionSummary={(() => {
                                          const names = resolveAssignToSelectionNames({
                                            isHr: false,
                                            selectedIds: formData.selectedCandidates,
                                            collegeTemplates,
                                            employees: filteredEmployees,
                                          });
                                          return names.length > 0 ? (
                                            <RoleSelectionSummary names={names} />
                                          ) : null;
                                        })()}
                                        toggleAriaLabel="Toggle candidates list"
                                      />

                                      <GroupsPickerDropdownPortal
                                        ref={employeeDropdownPanelRef}
                                        isOpen={isEmployeeDropdownOpen}
                                        anchorRef={dropdownRef}
                                        minWidth={256}
                                        title={isHrGroupRow ? "External Setup Groups" : "Group Members"}
                                        subtitle={isHrGroupRow ? "College Template Groups" : "Candidate List"}
                                        count={
                                          Array.isArray(formData.selectedCandidates)
                                            ? formData.selectedCandidates.length
                                            : 0
                                        }
                                      >
                                          <div className="assess-mod-groups-member-panel__list">
                                            {isHrGroupRow ? (
                                              <>
                                                <GroupsPickerMemberRow
                                                  isSelectAll
                                                  selectAllLabel="Select All Groups"
                                                  checked={(() => {
                                                    const visible = getVisibleCollegeTemplates();
                                                    return visible.length > 0 && visible.every(t => isCollegeTemplateSelected(t));
                                                  })()}
                                                  onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    const visible = getVisibleCollegeTemplates();
                                                    const allStudentIds = visible.flatMap(t => (t.student_ids || []).map(String));
                                                    setFormData(prev => {
                                                      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates.map(String) : [];
                                                      const updated = checked
                                                        ? [...new Set([...current, ...allStudentIds])]
                                                        : current.filter(id => !allStudentIds.includes(id));
                                                      const currentRoles = Array.isArray(prev.user_role) ? prev.user_role.map(String) : [];
                                                      const visibleTemplateIds = visible.map(t => String(t.id));
                                                      const updatedRoles = checked
                                                        ? [...new Set([...currentRoles, ...visibleTemplateIds])]
                                                        : currentRoles.filter(r => !visibleTemplateIds.includes(r));
                                                      return { ...prev, selectedCandidates: updated, candidates: updated.join(','), user_role: updatedRoles };
                                                    });
                                                  }}
                                                />
                                                {getVisibleCollegeTemplates().map(template => (
                                                  <GroupsPickerMemberRow
                                                    key={template.id}
                                                    name={template.name}
                                                    meta={`${(template.student_ids || []).length} Students`}
                                                    checked={isCollegeTemplateSelected(template)}
                                                    onChange={() => toggleCollegeTemplate(template)}
                                                  />
                                                ))}
                                              </>
                                            ) : (
                                              <>
                                                <GroupsPickerMemberRow
                                                  isSelectAll
                                                  selectAllLabel="Select All Members"
                                                  checked={(() => {
                                                    const visible = filteredEmployees.filter(emp =>
                                                      emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                                      (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
                                                      (emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
                                                    );
                                                    return visible.length > 0 && visible.every(emp => Array.isArray(formData.selectedCandidates) && formData.selectedCandidates.map(String).includes(String(emp.id)));
                                                  })()}
                                                  onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    const visible = filteredEmployees.filter(emp =>
                                                      emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                                      (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
                                                      (emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
                                                    );
                                                    const visibleIds = visible.map(emp => String(emp.id));
                                                    setFormData(prev => {
                                                      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates.map(String) : [];
                                                      const updated = checked
                                                        ? [...new Set([...current, ...visibleIds])]
                                                        : current.filter(id => !visibleIds.includes(id));
                                                      return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
                                                    });
                                                  }}
                                                />
                                                {filteredEmployees
                                                  .filter(emp =>
                                                    emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                                    (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
                                                    (emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
                                                  )
                                                  .map(emp => (
                                                    <GroupsPickerMemberRow
                                                      key={emp.id}
                                                      name={emp.name}
                                                      meta={emp.email || emp.job_role}
                                                      checked={Array.isArray(formData.selectedCandidates) && formData.selectedCandidates.map(String).includes(String(emp.id))}
                                                      onChange={() => toggleEmployee(emp.id)}
                                                    />
                                                  ))
                                                }
                                              </>
                                            )}
                                          </div>
                                          {((isHrGroupRow ? getVisibleCollegeTemplates() : filteredEmployees.filter(emp =>
                                            emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                                            (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
                                            (groupRolesSearchTerm && emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
                                          )).length === 0) && (
                                            <p className="assess-mod-groups-member-panel__empty">
                                              {isHrGroupRow ? "No college templates found" : "No candidates found"}
                                            </p>
                                          )}
                                      </GroupsPickerDropdownPortal>
                                    </div>
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-date align-middle">
                                    <div className={`relative flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm hover:border-slate-300 transition-all w-full max-w-[150px] min-w-[130px] h-[36px] ${inlineLocked ? 'opacity-80 cursor-default' : ''}`}>
                                      <span className="text-xs font-bold text-slate-700 tracking-wide select-none">
                                        {formatDateToDDMMYYYY(formData.scheduled_date) || 'Select Date'}
                                      </span>
                                      <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <input
                                        type="date"
                                        disabled={inlineLocked}
                                        className="assess-mod-groups-datetime-input absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        value={formData.scheduled_date}
                                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                                      />
                                    </div>
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-start align-middle">
                                    <TimePicker12Hour
                                      disabled={inlineLocked}
                                      value={formData.start_time}
                                      onChange={(val) => handleTimeChange('start_time', val)}
                                    />
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-end align-middle">
                                    <TimePicker12Hour
                                      disabled={inlineLocked}
                                      value={formData.end_time}
                                      onChange={(val) => handleTimeChange('end_time', val)}
                                    />
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-duration align-middle">
                                    <div className="assess-mod-groups-duration-wrap">
                                      <input
                                        type="number"
                                        readOnly
                                        className="bg-transparent border-0 focus:ring-0 outline-none"
                                        value={formData.duration}
                                      />
                                      <span className="text-xs font-bold text-slate-400">M</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-5 assess-mod-groups-col-action align-middle">
                                    <div className="assess-mod-groups-action-wrap">
                                      {!savedPackId && (
                                        <button 
                                          type="button"
                                          onClick={() => handleSaveInline(row.original.id)}
                                          className="assess-mod-groups-action-btn bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white group"
                                          title="Save"
                                        >
                                          <FiSave className="group-hover:scale-110 transition-transform" />
                                        </button>
                                      )}
                                      {savedPackId && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => handleToggleInlineEdit(row.original.id)}
                                            className={`assess-mod-groups-action-btn group ${inlineIsEditing ? 'bg-emerald-500 text-white' : 'bg-[#f0fdf4] text-[#10b981] hover:bg-[#d1fae5]'}`}
                                            title={inlineIsEditing ? 'Save Changes' : 'Edit Group'}
                                          >
                                            {inlineIsEditing ? (
                                              <FiCheck className="group-hover:scale-110 transition-transform" />
                                            ) : (
                                              <AssessmentEditIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                            )}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setIsPreviewOpen(true)}
                                            className="assess-mod-groups-action-btn bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white group"
                                            title="Preview Question Paper"
                                          >
                                            <FiEye className="group-hover:scale-110 transition-transform" />
                                          </button>
                                          {!inlineIsEditing && (
                                            <button
                                              type="button"
                                              onClick={() => handlePublishInline(savedPackId, row.original.id)}
                                              className="assess-mod-groups-action-btn bg-amber-50 text-amber-500 hover:bg-amber-400 hover:text-white group"
                                              title="Publish"
                                            >
                                              <FiSend className="group-hover:scale-110 transition-transform" />
                                            </button>
                                          )}
                                        </>
                                      )}
                                      <button 
                                        type="button"
                                        onClick={handleDeleteInline}
                                        className="assess-mod-groups-action-btn assess-mod-inventory-action-btn assess-mod-inventory-action-btn--red group"
                                        title={savedPackId ? 'Delete Group' : 'Cancel'}
                                      >
                                        <FiTrash2 className="group-hover:scale-110 transition-transform" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                );
                              })()}
                              {paginatedPacks.map((batch, idx) => {
                                return (
                                  <BatchRow 
                                    key={batch.id} 
                                    batch={resolvePackForRow(row, batch.id, batch) || batch} 
                                    assessmentId={row.original.id}
                                    onRefresh={refreshData}
                                    onPackDeleted={handlePackDeletedLocally}
                                    assignedRoles={batch.user_role || ''}
                                    rolesList={rolesList}
                                    assessmentCategory={row.original.category}
                                    templates={templates} 
                                    onPreview={(b) => handlePreview(b, row.original.id)}
                                    assessment={row.original}
                                  />
                                );
                              })}
                              {((row.original.isLocal && (!row.original.groups || row.original.groups.length === 0)) || (!row.original.isLocal && (!row.original.test_packs || row.original.test_packs.length === 0))) && activeFormId !== row.original.id && (
                                <tr>
                                  <td colSpan={groupColSpan} className="py-16 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                      <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
                                        <FiBox className="text-slate-200 text-xl" />
                                      </div>
                                      <p className="assess-mod-groups-empty text-slate-400 uppercase tracking-wide">No groups assigned yet</p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          <GroupsAccordionPagination
                            currentPage={safeGroupPage + 1}
                            totalPages={totalGroupPages}
                            totalItems={sortedPacks.length}
                            pageSize={GROUPS_PAGE_SIZE}
                            onPageChange={(page) =>
                              setGroupsPageIndex(row.original.id, page - 1)
                            }
                          />
                              </>
                            );
                          })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      
      <div className="px-6 py-4 flex justify-between items-center bg-white border-t border-slate-100">
        <div className="dataTables_info assess-mod-inventory-pagination font-semibold text-slate-500" role="status" aria-live="polite">
          Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, data.length)} of {data.length} entries
        </div>
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              !table.getCanPreviousPage() 
                ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed' 
                : 'bg-white border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500 shadow-sm'
            }`}
          >
            <FiChevronLeft className="text-xs" />
            Previous
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1.5 mx-1">
            {table.getPageOptions().map((pageIndex) => {
              const isCurrentPage = table.getState().pagination.pageIndex === pageIndex;
              return (
                <button
                  key={pageIndex}
                  onClick={() => table.setPageIndex(pageIndex)}
                  className={`assess-mod-inventory-pagination w-9 h-9 flex items-center justify-center rounded-xl text-sm font-bold transition-all ${
                    isCurrentPage 
                      ? 'bg-red-600 text-white shadow-lg shadow-red-100' 
                      : 'bg-red-50/50 text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100'
                  }`}
                >
                  {pageIndex + 1}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              !table.getCanNextPage() 
                ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed' 
                : 'bg-white border-slate-100 text-slate-700 hover:border-red-200 hover:text-red-500 shadow-sm font-bold'
            }`}
          >
            Next
            <FiChevronRight className="text-xs" />
          </button>
        </div>
      </div>
      <AssessmentPopup
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
      <IntroVideoPreviewModal
        isOpen={introVideoModal.isOpen}
        onClose={handleCloseIntroVideo}
        testName={introVideoModal.testName}
        videos={introVideoModal.videos}
      />
      <QuestionPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        packId={savedPackId}
        batchName={formData.pack_name}
        packSnapshot={
          savedPackId
            ? (() => {
                const tpl = templates.find((t) => String(t.id) === String(formData.template_id));
                return {
                  id: savedPackId,
                  pack_name: formData.pack_name,
                  template_id: formData.template_id,
                  template_name: tpl?.name,
                  duration: formData.duration,
                  instructions: formData.instructions,
                  template: tpl,
                  sections: tpl?.sections || [],
                  questions: tpl?.questions || [],
                };
              })()
            : null
        }
        assessment={activeAssessment}
      />
    </div>
  );
};

export default TestsDataTable;
